import axios from 'axios';

import { APP_CONFIG } from '@/constants/app';
import type {
  BillingDashboardResponse,
  BillingDashboardData,
  CurrentMonthUsageResponse,
  InvoicesListResponse,
  InvoiceDetailResponse,
  UsageHistoryResponse,
  MonthlyBillingStatus,
} from '@/types/billing.types';
import { tokenManager } from '@/utils/token-manager';

// Create axios instance for billing service
const billingApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/billing',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(billingApi);

export const billingService = {
  // Dashboard
  getDashboard: async (): Promise<BillingDashboardData> => {
    const response =
      await billingApi.get<BillingDashboardResponse>('/dashboard');
    return response.data.data;
  },

  // Current month usage
  getCurrentMonthUsage: async (): Promise<CurrentMonthUsageResponse> => {
    const response = await billingApi.get<{ data: CurrentMonthUsageResponse }>(
      '/usage/current-month',
    );
    return response.data.data;
  },

  // Usage history
  getUsageHistory: async (
    months: number = 6
  ): Promise<UsageHistoryResponse> => {
    const response = await billingApi.get<{ data: UsageHistoryResponse }>(
      '/usage/history',
      {
        params: { months },
      }
    );
    return response.data.data;
  },

  // Invoices
  getInvoices: async (
    page: number = 1,
    limit: number = 10,
    status?: MonthlyBillingStatus,
  ): Promise<InvoicesListResponse> => {
    const response = await billingApi.get<{ data: InvoicesListResponse }>(
      '/invoices',
      {
        params: { page, limit, status },
      }
    );
    return response.data.data;
  },

  // Invoice details
  getInvoiceDetails: async (uuid: string): Promise<InvoiceDetailResponse> => {
    const response = await billingApi.get<{ data: InvoiceDetailResponse }>(
      `/invoices/${uuid}`,
    );
    return response.data.data;
  },

  // SEPA Mandate / SetupIntent
  createSepaSetupIntent: async (): Promise<{
    clientSecret: string;
    setupIntentId: string;
    publishableKey: string;
  }> => {
    const response = await billingApi.post<{
      data: { clientSecret: string; setupIntentId: string; publishableKey: string };
    }>('/sepa/setup-intent', {});
    return response.data.data;
  },

  // Rattache au compte le moyen de paiement que Stripe vient de confirmer,
  // sans attendre le webhook. Rend le statut à jour.
  confirmSepaSetup: async (
    setupIntentId: string
  ): Promise<SepaStatusResponse> => {
    const response = await billingApi.post<{ data: SepaStatusResponse }>(
      '/sepa/confirm',
      { setupIntentId }
    );
    return response.data.data;
  },

  getSepaStatus: async (): Promise<SepaStatusResponse> => {
    const response = await billingApi.get<{
      data: SepaStatusResponse;
    }>('/sepa/status');
    return response.data.data;
  },
};

export interface CreateSepaSetupIntentRequest {
  locale?: string;
}

/**
 * Statut du moyen de paiement (route historique `/sepa/status`).
 * [2026-09] Carte bancaire attendue ; les mandats SEPA déjà signés restent
 * valides. `hasValidMandate` = « un moyen de paiement utilisable est enregistré ».
 */
export interface SepaStatusResponse {
  hasValidMandate: boolean;
  /** Compte interne ou de test : jamais facturé, aucun moyen de paiement demandé. */
  exempt?: boolean;
  /** Faux pour un simple membre d'un cabinet : seul le propriétaire (ou un administrateur) pose la carte. */
  canManage?: boolean;
  paymentMethodType?: 'card' | 'sepa_debit' | null;
  isInGracePeriod?: boolean;
  gracePeriodEnd?: string | null;
  mandateStatus: string | null;
  lastFour: string | null;
  bankName: string | null;
  /** Carte : marque (visa, mastercard…) et échéance MM/AA. */
  brand?: string | null;
  expiry?: string | null;
}

export interface CreateSepaSetupIntentResponse {
  clientSecret: string;
  setupIntentId: string;
  publishableKey: string;
}
